import { i18nextT } from "hooks/i18n/i18n";
import videojs from "video.js";

import {
  attachNativePictureInPictureSeekWorkaround,
  createTheoPictureInPictureController,
  createVideoElementPictureInPictureController,
} from "./pictureInPicture";
import { getVideoJsComponent } from "./utils";

import type {
  PictureInPictureController,
  TheoPlayerForPictureInPicture,
} from "./pictureInPicture";
import type { THEOPlayerRuntime } from "./theoplayerRuntime";
import type Player from "video.js/dist/types/player";

/**
 * Figma「PiP」(2600:1477) と同じ形状。外枠 26x18 (線幅 3) の中に 12x8 の小窓。
 * PiP 中も同じアイコンで、背景色の有無 (vjs-custom-picture-in-picture-button-active) で状態を表す。
 */
const pictureInPictureIcon = `
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="6.5" y="10.5" width="23" height="15" stroke="white" stroke-width="3"/>
        <rect x="16" y="16" width="12" height="8" fill="white"/>
      </svg>
    `;

const PICTURE_IN_PICTURE_BUTTON_ACTIVE_CLASS =
  "vjs-custom-picture-in-picture-button-active";

type GetPictureInPictureButtonArgs =
  /**
   * THEOplayer は Presentation API の 'native-picture-in-picture' を使う。
   * ソース差し替え時の PiP 維持や iOS Safari の webkit API の差異は SDK 側で吸収される。
   */
  | {
      playerType: "THEOPlayer";
      runtime: THEOPlayerRuntime;
      theoPlayer: TheoPlayerForPictureInPicture;
    }
  /** IVS は素の video.js なので video 要素のブラウザ API を直接叩く */
  | { playerType: "IVS" };

export const getPictureInPictureButton = (
  args: GetPictureInPictureButtonArgs
) => {
  const VideoJSButton =
    args.playerType === "IVS"
      ? videojs.getComponent("Button")
      : getVideoJsComponent("Button", args.runtime);

  class PictureInPictureButton extends VideoJSButton {
    private readonly controller: PictureInPictureController;

    constructor(player: Player, options = {}) {
      super(player, options);

      this.controller =
        args.playerType === "THEOPlayer"
          ? createTheoPictureInPictureController(args.theoPlayer)
          : createVideoElementPictureInPictureController(player.el());

      const unsubscribe = this.controller.subscribe(
        this.syncPictureInPictureUI
      );
      const detachSeekWorkaround =
        args.playerType === "THEOPlayer"
          ? attachNativePictureInPictureSeekWorkaround(args.theoPlayer)
          : undefined;
      player.ready(this.syncPictureInPictureUI);

      this.on("dispose", () => {
        unsubscribe();
        detachSeekWorkaround?.();
      });

      this.syncPictureInPictureUI();
    }

    syncPictureInPictureUI = () => {
      if (!this.controller.isSupported()) {
        this.hide();
        return;
      }
      this.show();

      // ラベルは PiP 中も「ミニプレーヤー」のままなので、状態は aria-pressed で伝える
      const isActive = this.controller.isActive();
      if (isActive) {
        this.addClass(PICTURE_IN_PICTURE_BUTTON_ACTIVE_CLASS);
      } else {
        this.removeClass(PICTURE_IN_PICTURE_BUTTON_ACTIVE_CLASS);
      }
      this.el()?.setAttribute("aria-pressed", String(isActive));
    };

    handleClick() {
      this.controller.toggle();
    }

    createEl() {
      const label = i18nextT("streaming.controller.miniPlayer");
      // ツールチップは自前で出す (vjs-custom-button-tooltip) ので、ブラウザ標準の title は付けない
      const el = super.createEl("button", {
        className:
          "vjs-custom-picture-in-picture-button vjs-custom-button vjs-control vjs-button",
        innerHTML: `${pictureInPictureIcon}<span class="vjs-control-text">${label}</span><span class="vjs-custom-button-tooltip" aria-hidden="true">${label}</span>`,
      });
      return el;
    }
  }

  return PictureInPictureButton;
};
